import { AuthStep } from 'constants/authStep';

import { useState } from 'react';

import { Button } from '@mui/material';
import { useCaptcha } from 'hooks/useCaptcha';
import { useTypedTranslate } from 'providers/i18n/hooks/useTypedTranslate';
import { useAuthProvider } from 'react-admin';
import { FieldValues, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert } from 'shared/mui/Alert/Alert';
import { Box } from 'shared/mui/Box';
import { PasswordVisibilityIconButton } from 'shared/mui/IconButtons/PasswordVisibilityIconButton/PasswordVisibilityIconButton';
import { TextField } from 'shared/mui/TextField/TextField';
import { executeCaptchaWithData } from 'utils';

type Props = {
  updateStep: (step: AuthStep) => void;
  error?: string | null;
  setError: (error: string | null) => void;
};

export const LoginTempPasswordForm: React.FC<Props> = ({ updateStep, error, setError }) => {
  const translate = useTypedTranslate();

  useCaptcha();
  const authProvider = useAuthProvider();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isRepeatPasswordVisible, setIsRepeatPasswordVisible] = useState(false);

  const { register, handleSubmit, watch } = useForm();
  const newPassword = watch('newPassword');

  const hasError = Boolean(error);

  const submit = (captchaToken: string, { newPassword, repeatPassword }: FieldValues) => {
    if (newPassword !== repeatPassword) {
      setError(translate('app.login.passwordsDoNotMatch'));
      return;
    }
    authProvider
      ?.changePassword({
        captchaToken,
        new_password: newPassword,
        repeat_password: repeatPassword,
      })
      .then(() => {
        navigate('/login', { state });
        updateStep(authProvider.step);
      })
      .catch((reason: any) => {
        reason.data.errors.forEach((error: any) => {
          setError(error.title);
        });
      });
  };

  const proxySubmit = (data: FieldValues) => {
    setError(null);
    executeCaptchaWithData(submit, data);
  };

  return (
    <Box>
      {hasError && (
        <Alert severity="error" sx={{ marginBottom: '24px' }}>
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit(proxySubmit)}>
        <TextField
          {...register('newPassword')}
          InputProps={{
            endAdornment: (
              <PasswordVisibilityIconButton
                isVisible={isPasswordVisible}
                onClick={() => setIsPasswordVisible((value) => !value)}
              />
            ),
          }}
          error={hasError}
          fullWidth
          id="newPassword"
          label={translate('app.login.newPass')}
          margin="normal"
          size="medium"
          sx={{ marginBottom: '24px !important' }}
          type={isPasswordVisible ? 'text' : 'password'}
          variant="outlined"
        />
        <TextField
          {...register('repeatPassword')}
          InputProps={{
            endAdornment: (
              <PasswordVisibilityIconButton
                isVisible={isRepeatPasswordVisible}
                onClick={() => setIsRepeatPasswordVisible((value) => !value)}
              />
            ),
          }}
          error={hasError}
          fullWidth
          id="repeatPassword"
          label={translate('app.login.confirmPass')}
          margin="normal"
          size="medium"
          sx={{ marginBottom: '24px !important' }}
          type={isRepeatPasswordVisible ? 'text' : 'password'}
          variant="outlined"
        />
        <Box sx={{ display: 'flex', gap: '16px' }}>
          <Button fullWidth onClick={() => navigate('login')} variant="outlined">
            {translate('app.buttons.back')}
          </Button>
          <Button disabled={!newPassword?.length} fullWidth type="submit" variant="contained">
            {translate('app.buttons.continue')}
          </Button>
        </Box>
      </form>
    </Box>
  );
};
